/**
 * Density + i18n context — Compact / Normal / Comfortable, RTL, fr/ar/en.
 *
 * - Persists choice in localStorage
 * - Sets data-density / dir / lang attributes on <html>
 * - Used by every page to adapt spacing
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Density = "compact" | "normal" | "comfortable";
export type Locale = "fr" | "ar" | "en";

interface DensityCtxValue {
  density: Density;
  setDensity: (d: Density) => void;
  locale: Locale;
  setLocale: (l: Locale) => void;
  rtl: boolean;
}

const Ctx = createContext<DensityCtxValue | null>(null);

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => {
    try {
      const stored = localStorage.getItem("density");
      if (stored === "compact" || stored === "normal" || stored === "comfortable") return stored;
    } catch {}
    return "normal";
  });
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem("locale");
      if (stored === "fr" || stored === "ar" || stored === "en") return stored;
    } catch {}
    return "fr";
  });

  const rtl = locale === "ar";

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    document.documentElement.setAttribute("lang", locale);
    document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
  }, [density, locale, rtl]);

  const setDensity = (d: Density) => {
    setDensityState(d);
    try { localStorage.setItem("density", d); } catch {}
    // Track analytics event (lazy import to avoid circular deps)
    import("../lib/posthog").then(({ clinicalEvents }) => clinicalEvents.densityChanged(d)).catch(() => {});
  };
  const setLocale = (l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem("locale", l); } catch {}
    import("../lib/posthog").then(({ clinicalEvents }) => clinicalEvents.localeChanged(l)).catch(() => {});
  };

  return <Ctx.Provider value={{ density, setDensity, locale, setLocale, rtl }}>{children}</Ctx.Provider>;
}

export function useDensity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDensity must be used inside DensityProvider");
  return ctx;
}

/** CSS classes pour la densité active — appliquées sur le wrapper principal. */
export function densityClasses(d: Density) {
  switch (d) {
    case "compact": return "[&>*]:!p-3 [&_h1]:!text-xl [&_h2]:!text-lg [&_.text-sm]:!text-xs";
    case "comfortable": return "[&>*]:!p-8 [&_h1]:!text-3xl [&_h2]:!text-2xl";
    default: return "";
  }
}